"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Check, Loader2 } from "lucide-react";
import { useMembershipGate } from "./membership/MembershipGate";

/**
 * One-time blocking welcome shown on the member's first visit AFTER an agent
 * activates their live dashboard. Locks the dashboard behind the notice until they
 * press "I Understand", then persists the acknowledgement (server-side, so it never
 * shows again — even on another device). Rendered by the (dashboard) layout only
 * when live && !acknowledged && !preview.
 */
export function LiveDashboardWelcome() {
  const router = useRouter();
  const [shown, setShown] = React.useState(true);
  // A Basic member meets the membership page first; this welcome follows once that is closed.
  const { upsellOpen } = useMembershipGate();
  const [busy, setBusy] = React.useState(false);

  // Lock page scroll while the notice blocks the dashboard.
  React.useEffect(() => {
    if (!shown) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [shown]);

  if (!shown || upsellOpen) return null;

  async function acknowledge() {
    if (busy) return;
    setBusy(true);
    try {
      await fetch("/api/member/ack-live-welcome", { method: "POST" });
    } catch {
      /* best-effort: if it fails to persist, the notice simply reappears next login */
    }
    setShown(false);
    router.refresh();
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-navy-500/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="live-welcome-title"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl">
        {/* Header band */}
        <div className="relative overflow-hidden bg-gradient-to-br from-cj-800 to-violet-600 px-7 pt-7 pb-6 text-white">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/cj-owl/owl-wave-green.png"
            alt=""
            className="pointer-events-none absolute -right-2 -top-1 w-24 select-none opacity-90"
          />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Welcome</p>
          <h2 id="live-welcome-title" className="mt-1 max-w-[80%] text-[24px] font-extrabold leading-tight">
            Your CJ OWL Dashboard is Now Live
          </h2>
        </div>

        {/* Body */}
        <div className="space-y-4 px-7 py-6 text-[14px] leading-relaxed text-gray-600">
          <p>
            Your dashboard has been activated and will now display your actual household or business information as our
            Savings Team reviews your services.
          </p>
          <p>
            Some sections may continue to update over the next few days as we analyze your accounts and identify savings
            opportunities.
          </p>
          <p className="font-semibold text-navy-500">Please review the information carefully before continuing.</p>
        </div>

        {/* Acknowledge */}
        <div className="px-7 pb-7">
          <button
            onClick={acknowledge}
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-leaf-500 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-leaf-600 disabled:opacity-70"
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" /> I Understand
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
